import { useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import { publicApi } from "@/services/publicApi";
import { mapListingToCard, categoryFilters } from "@/services/listingMapper";
import type { ListingCard } from "@/services/listingMapper";
import { useApiData } from "@/hooks/useApiData";
import { usePageMeta } from "@/hooks/usePageMeta";

// Stats are now derived dynamically from listing pagination total
const STATIC_STATS = [
  { value: "450+", label: "Land Parcels Listed", icon: "ri-landscape-line" },
  { value: "₹500 Cr+", label: "Lease Value Generated", icon: "ri-money-rupee-circle-line" },
  { value: "3-4 Weeks", label: "Average Deal Closure", icon: "ri-timer-flash-line" },
  { value: "200+", label: "Verified Tenants", icon: "ri-shield-check-line" },
];

// ── City name variants for matching location strings ──
const CITY_ALIASES: Record<string, string[]> = {
  Bengaluru: ["Bengaluru", "Bangalore", "Bengaluru Rural", "North Bengaluru", "Bengaluru East"],
  Mysuru: ["Mysuru", "Mysore"],
  Hubli: ["Hubli", "Hubballi"],
  Mangaluru: ["Mangaluru", "Mangalore"],
  Belagavi: ["Belagavi", "Belgaum"],
  Ballari: ["Ballari", "Bellary"],
  Kalaburagi: ["Kalaburagi", "Gulbarga"],
  Shivamogga: ["Shivamogga", "Shimoga"],
  Udupi: ["Udupi"],
  Tumakuru: ["Tumakuru", "Tumkur"],
  Vijayapura: ["Vijayapura", "Bijapur"],
  Davanagere: ["Davanagere", "Davangere"],
};

function listingMatchesCity(location: string, city: string, cityField?: string): boolean {
  const aliases = CITY_ALIASES[city] || [city];
  const searchIn = [location.toLowerCase()];
  if (cityField) searchIn.push(cityField.toLowerCase());
  return aliases.some((alias) => searchIn.some((s) => s.includes(alias.toLowerCase())));
}

function ListingCardSkeleton() {
  return (
    <div className="rounded-2xl card-dark overflow-hidden animate-pulse">
      <div className="aspect-[4/3] bg-background-200/50" />
      <div className="p-5 space-y-3">
        <div className="h-3 bg-background-200/50 rounded w-1/2" />
        <div className="h-5 bg-background-200/50 rounded w-3/4" />
        <div className="h-4 bg-background-200/50 rounded w-full" />
        <div className="grid grid-cols-2 gap-3">
          <div className="h-14 bg-background-200/50 rounded-xl" />
          <div className="h-14 bg-background-200/50 rounded-xl" />
        </div>
      </div>
    </div>
  );
}

export default function ListingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const cityFilter = searchParams.get("city") || "";

  usePageMeta({
    title: cityFilter
      ? `Land for Lease & Sale in ${cityFilter} | CashMyProperty.ai`
      : "Land Listings in Karnataka — Lease, Rent & Buy Verified Land | CashMyProperty.ai",
    description: cityFilter
      ? `Browse verified land parcels for lease, rent and sale in ${cityFilter}, Karnataka. AI-vetted commercial, agricultural and industrial land with expected rent and ROI data.`
      : "Browse 450+ AI-vetted land parcels across Karnataka. Commercial, agricultural, industrial and mixed-use land for lease, rent and sale with expected rent and ROI data.",
    canonicalUrl: cityFilter
      ? `https://cashmyproperty.ai/listings?city=${encodeURIComponent(cityFilter)}`
      : "https://cashmyproperty.ai/listings",
  });

  const [activeFilter, setActiveFilter] = useState("All");
  const [page, setPage] = useState(1);
  const perPage = 9;

  const apiParams = useMemo(() => {
    const params: Record<string, string | number> = { page, per_page: perPage };
    if (activeFilter !== "All") params.transaction_type = activeFilter;
    if (cityFilter) params.search = cityFilter;
    return params;
  }, [activeFilter, page, cityFilter]);

  const { data: apiResponse, loading, error } = useApiData(
    () => publicApi.getListings(apiParams),
    [apiParams]
  );

  const { data: settingsResponse } = useApiData(
    () => publicApi.getSettings(),
    []
  );

  const showPrices = useMemo(() => {
    const features = settingsResponse?.data?.features as Record<string, unknown> | undefined;
    return features?.show_prices !== false;
  }, [settingsResponse]);

  const allListings: ListingCard[] = useMemo(() => {
    if (!apiResponse?.data) return [];
    return apiResponse.data.map(mapListingToCard);
  }, [apiResponse]);

  const listings: ListingCard[] = useMemo(() => {
    if (!cityFilter) return allListings;
    return allListings.filter((l) =>
      listingMatchesCity(l.location, cityFilter, l.city)
    );
  }, [allListings, cityFilter]);

  const pagination = apiResponse?.pagination;
  const totalPages = pagination?.totalPages || 1;
  const totalResults = pagination?.total || 0;

  const dynamicStats = [
    { value: totalResults > 0 ? `${totalResults}+` : '450+', label: 'Land Parcels Listed', icon: 'ri-landscape-line' },
    { value: '₹500 Cr+', label: 'Lease Value Generated', icon: 'ri-money-rupee-circle-line' },
    { value: '3-4 Weeks', label: 'Average Deal Closure', icon: 'ri-timer-flash-line' },
    { value: '200+', label: 'Verified Tenants', icon: 'ri-shield-check-line' },
  ];
  const stats = totalResults > 0 ? dynamicStats : STATIC_STATS;

  const clearCityFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("city");
    setSearchParams(next, { replace: true });
    setPage(1);
  };

  return (
    <PageLayout>
      {/* Stats bar */}
      <section className="py-10 bg-background-100 border-b border-primary-900/30">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-background-50 border border-primary-500/30 mx-auto mb-3">
                  <i className={`${s.icon} text-xl text-primary-500`} />
                </div>
                <div className="text-2xl md:text-3xl font-display font-bold text-foreground-950">{s.value}</div>
                <div className="text-sm text-foreground-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Category filters + city chip */}
      <section className="py-8 bg-background-50 border-b border-primary-900/20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-wrap items-center gap-3">
            {categoryFilters.map((f) => (
              <button
                key={f.value}
                onClick={() => {
                  setActiveFilter(f.value);
                  setPage(1);
                }}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeFilter === f.value
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-200/80 text-foreground-600 hover:text-foreground-950 hover:bg-background-300/80 border border-primary-800/20"
                }`}
              >
                <i className={f.icon} />
                {f.label}
              </button>
            ))}

            {cityFilter && (
              <button
                onClick={clearCityFilter}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium bg-accent-500/10 text-accent-700 border border-accent-500/30 hover:bg-accent-500/20 transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-map-pin-line" />
                {cityFilter}
                <i className="ri-close-line ml-0.5" />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Listings grid */}
      <section className="py-16 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8">
          {loading ? (
            <>
              <div className="flex items-center justify-between mb-8">
                <div className="h-5 bg-background-200/50 rounded w-48 animate-pulse" />
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {Array.from({ length: 6 }).map((_, i) => (
                  <ListingCardSkeleton key={i} />
                ))}
              </div>
            </>
          ) : error ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-rose-500/10 mx-auto mb-5">
                <i className="ri-error-warning-line text-2xl text-rose-500" />
              </div>
              <h3 className="text-xl font-display font-semibold text-foreground-950 mb-2">Failed to load listings</h3>
              <p className="text-foreground-500 mb-6 max-w-md mx-auto">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line" />
                Try Again
              </button>
            </div>
          ) : listings.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-50 border border-primary-500/30 mx-auto mb-5">
                <i className="ri-search-line text-2xl text-primary-500" />
              </div>
              <h3 className="text-xl font-display font-semibold text-foreground-950 mb-2">No listings found</h3>
              <p className="text-foreground-500">
                {cityFilter
                  ? `No listings match "${cityFilter}" with the current filters. Try clearing the city or transaction type filter.`
                  : "Try a different transaction type filter or check back soon for new listings."}
              </p>
              {cityFilter && (
                <button
                  onClick={clearCityFilter}
                  className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-primary-500/30 text-primary-500 text-sm font-medium hover:bg-primary-500/10 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-close-line" />
                  Clear city filter
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
                <p className="text-sm text-foreground-500">
                  Showing <span className="text-foreground-950 font-medium">{listings.length}</span>{" "}
                  {cityFilter && (
                    <span>
                      in <span className="text-foreground-950 font-medium">{cityFilter}</span>
                    </span>
                  )}
                  {activeFilter !== "All" && (
                    <span>
                      {" "}— <span className="text-foreground-950 font-medium">{categoryFilters.find(f => f.value === activeFilter)?.label || activeFilter}</span>
                    </span>
                  )}
                </p>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {listings.map((listing) => (
                  <div
                    key={listing.id}
                    className="group rounded-2xl card-dark overflow-hidden hover:border-primary-500/50 transition-all duration-300 flex flex-col"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <Link to={`/listings/${listing.id}`} className="block w-full h-full cursor-pointer">
                        <img
                          src={listing.images[0] || "https://readdy.ai/api/search-image?query=Modern%20minimal%20real%20estate%20placeholder%20image%2C%20abstract%20geometric%20land%20parcel%20shape%2C%20soft%20gradient%20neutral%20background%2C%20warm%20cream%20and%20light%20gold%20tones%2C%20subtle%20grid%20lines%2C%20clean%20professional%20real%20estate%20marketing%20aesthetic&width=800&height=600&seq=listing-fallback&orientation=landscape"}
                          alt={listing.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </Link>
                      <div className="absolute top-3 left-3">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${listing.statusColor}`}>
                          {listing.status}
                        </span>
                      </div>
                      <div className="absolute top-3 right-3">
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-background-950/60 backdrop-blur-sm text-background-50">
                          {listing.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <div className="flex items-center gap-2 text-xs text-foreground-500 mb-2">
                        <i className="ri-map-pin-line text-primary-500" />
                        {listing.location}
                      </div>
                      <h3 className="font-display font-semibold text-base text-foreground-950 mb-2 group-hover:text-primary-500 transition-colors leading-snug">
                        <Link to={`/listings/${listing.id}`} className="hover:text-primary-500 transition-colors">
                          {listing.title}
                        </Link>
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed mb-4 flex-1 line-clamp-2">
                        {listing.description}
                      </p>
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="p-3 rounded-xl bg-background-200/50 border border-primary-800/20">
                          <div className="text-xs text-foreground-500 mb-0.5">Size</div>
                          <div className="text-sm font-semibold text-foreground-950">{listing.sizeDisplay}</div>
                        </div>
                        {showPrices && (
                          <div className="p-3 rounded-xl bg-background-200/50 border border-primary-800/20">
                            <div className="text-xs text-foreground-500 mb-0.5">Expected Rent</div>
                            <div className="text-sm font-semibold text-primary-600">{listing.expectedRentDisplay}</div>
                          </div>
                        )}
                        {!showPrices && (
                          <div className="p-3 rounded-xl bg-background-200/50 border border-primary-800/20">
                            <div className="text-xs text-foreground-500 mb-0.5">Price</div>
                            <div className="text-sm font-semibold text-foreground-400">Contact for price</div>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {listing.useCases.slice(0, 4).map((uc) => (
                          <span
                            key={uc}
                            className="inline-block px-2.5 py-1 rounded-md text-xs bg-primary-500/8 text-primary-700 border border-primary-700/20"
                          >
                            {uc}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center justify-between pt-3 border-t border-primary-900/20">
                        <span className="text-xs text-foreground-500 flex items-center gap-1">
                          <i className="ri-time-line" />
                          {listing.postedDate}
                        </span>
                        <Link
                          to={`/listings/${listing.id}`}
                          className="inline-flex items-center gap-1 text-xs text-primary-600 font-medium hover:text-primary-500 transition-colors whitespace-nowrap"
                        >
                          View Details
                          <i className="ri-arrow-right-line" />
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-12">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="w-10 h-10 flex items-center justify-center rounded-full border border-primary-800/20 text-foreground-500 hover:border-primary-500/30 hover:text-primary-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-10 h-10 flex items-center justify-center rounded-full text-sm font-medium transition-colors cursor-pointer ${
                        p === page
                          ? "bg-primary-500 text-background-50"
                          : "border border-primary-800/20 text-foreground-500 hover:border-primary-500/30 hover:text-primary-400"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="w-10 h-10 flex items-center justify-center rounded-full border border-primary-800/20 text-foreground-500 hover:border-primary-500/30 hover:text-primary-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Dual CTA */}
      <section className="py-24 bg-background-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-sm text-primary-500 font-medium uppercase tracking-wider mb-3">
              How It Works
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
              A Marketplace Built for Both Sides
            </h2>
            <p className="text-foreground-600">
              Whether you own land or need land — we make the connection seamless, fast, and transparent.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <div className="p-8 rounded-2xl card-dark text-center group hover:border-primary-500/50 transition-all duration-300">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-50 border border-primary-500/30 mx-auto mb-6 group-hover:border-primary-500/60 transition-colors">
                <i className="ri-user-star-line text-2xl text-primary-500" />
              </div>
              <h3 className="font-display font-semibold text-xl text-foreground-950 mb-3">For Landowners</h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                List your land for free. Our AI matches it with verified businesses actively looking for space.
              </p>
              <ul className="space-y-2 mb-8 text-left max-w-xs mx-auto">
                {["Free listing — pay only on deal closure", "AI-matched with verified tenants", "Professional photography & marketing", "Legal & documentation support included", "3-4 weeks average deal closure"].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-foreground-600">
                    <i className="ri-check-line text-primary-500 shrink-0 mt-0.5" />{item}
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
                List Your Land — It&apos;s Free <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="p-8 rounded-2xl card-dark text-center group hover:border-primary-500/50 transition-all duration-300">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-secondary-500/10 mx-auto mb-6 group-hover:bg-secondary-500/20 transition-colors">
                <i className="ri-building-2-line text-2xl text-secondary-500" />
              </div>
              <h3 className="font-display font-semibold text-xl text-foreground-950 mb-3">For Businesses</h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                Looking for the right location? Browse our AI-vetted land listings with complete feasibility data.
              </p>
              <ul className="space-y-2 mb-8 text-left max-w-xs mx-auto">
                {["450+ AI-vetted land parcels", "Location scoring & feasibility data", "Direct owner introductions", "Negotiation support included", "Post-lease compliance monitoring"].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-foreground-600">
                    <i className="ri-check-line text-secondary-500 shrink-0 mt-0.5" />{item}
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border border-secondary-500/40 text-secondary-400 font-semibold hover:bg-secondary-500/10 transition-colors whitespace-nowrap">
                Find Land for Your Business <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-background-50">
        <div className="container mx-auto px-4 lg:px-8 text-center max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground-950 mb-4">
            Don&apos;t See What You Need?
          </h2>
          <p className="text-foreground-600 mb-8">
            Many premium listings are matched privately before they hit the marketplace. Submit your requirements and get early access.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
              Get Early Access <i className="ri-arrow-right-line" />
            </Link>
            <Link to="/feasibility-report" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-primary-700/50 text-primary-400 font-semibold hover:bg-primary-500/10 transition-colors whitespace-nowrap">
              Get AI Feasibility Report
            </Link>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}